import { useState } from 'react';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconRemove } from '@components/icons/remove';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCancelJourneyChanges, useUnpublishedChanges } from 'api/journeys/hooks';
import { ActionChangesSection } from 'ui/authenticated/main/pages/journeys/sidebars/version-helpers';

export const useCancelChangesModal = () => useModal(CancelChanges);

const CancelChanges = ({ journeyShortId }: { journeyShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const [confirm, setConfirm] = useState(false);

  const cancelChanges = useCancelJourneyChanges({ onSuccess: hideModal });
  const { data: unpublishedChanges, isLoading } = useUnpublishedChanges(journeyShortId);

  const hasChanges = unpublishedChanges?.hasChanges ?? false;
  const summary = unpublishedChanges?.summary;
  const comparison = unpublishedChanges
    ? {
        summary: {
          added: unpublishedChanges.summary.removed,
          removed: unpublishedChanges.summary.added,
          modified: unpublishedChanges.summary.modified,
        },
        addedActions: unpublishedChanges.removedActions,
        removedActions: unpublishedChanges.addedActions,
        modifiedActions: unpublishedChanges.modifiedActions,
      }
    : undefined;

  return (
    <Modal>
      <Modal.SimpleHeader title={t('cancelChanges')} />
      <Modal.Content>
        <Stack space={6}>
          <Stack space={4}>
            <Text font="regular14" text={t('areYouSure')} />
            <Text font="regular14" color="elementRed200" text={t('cannotBeUndone')} />
          </Stack>

          {isLoading && <Text text={t('loadingChanges')} />}

          {hasChanges && summary && comparison && (
            <>
              <Stack space={3}>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="bold14" color="textPrimary" text={t('changesSummary')} />
                  <Badge text={summary.added + summary.removed + summary.modified} />
                </Stack>
                <Separator />
                <ActionChangesSection
                  summary={comparison.summary}
                  addedActions={comparison.addedActions}
                  modifiedActions={comparison.modifiedActions}
                  removedActions={comparison.removedActions}
                />
              </Stack>
            </>
          )}

          <Stack space={2}>
            <Text font="semibold14" text={t('pleaseConfirm')} />
            <Checkbox label={t('iUnderstand')} onChange={(value) => setConfirm(value)} />
          </Stack>
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
            <Button
              isDisabled={!confirm}
              onClick={() => cancelChanges.mutateAsync(journeyShortId)}
              disabled={cancelChanges.isLoading}
              variant="primary-red"
              iconLeft={<IconRemove />}
              text={t('remove')}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const locale = {
  en: {
    cancelChanges: 'Cancel changes',
    areYouSure:
      'Are you sure you want to cancel the changes that have been made since the journey was last published?',
    cannotBeUndone: 'This can not be undone.',
    pleaseConfirm: 'Please confirm',
    iUnderstand: 'I understand that my changes will be discarded',
    remove: 'Confirm',
    loadingChanges: 'Loading changes...',
    changesSummary: 'Changes summary',
    actionsAdded: 'Actions added',
    actionsModified: 'Actions modified',
    actionsRemoved: 'Actions removed',
    noDescription: 'No description',
  },
};
